/**
 * This component works together with the information streamed from the store to accomplish functionality
 * related with the authentication in NewHomeSource using Google One Tap as the provider.
 * Interactions with Google One Tap are encapsulated in the googleOneTapLibrary.js facade, use this
 * component as the link between the interaction of the user in the page and that facade.
 */

import { store$, store } from '@redux/store';
import LoadingStatus from 'enums/loadingStatus';
import { combineLatest, distinctUntilChanged, filter, fromEvent, map, take } from 'rxjs';
import { hideGoogleOneTapModal, loadGoogleOneTap, showGoogleOneTapModal } from 'libraries/googleOneTapLibrary';
import { selectConfigurationGoogleOneTapClientId, selectModal } from 'layout/redux/selectors';
import { selectIsLoggedIn, selectGoogleOneTapLibraryLoadingStatus } from '../redux/selectors';
import { authenticationSlice } from '../redux/slices';
import loginUserWithGoogleOneTap from '../apis/loginWithGoogleOneTap';
import parseJsonWebToken from '../services/parseJsonWebToken';

const authActions = authenticationSlice.actions;

const onLibraryLoaded = () => store.dispatch(authActions.googleOneTapLibraryLoadingStatus(LoadingStatus.Succeeded));
const onLibraryErrored = () => store.dispatch(authActions.googleOneTapLibraryLoadingStatus(LoadingStatus.Failed));

const onPageLoaded$ = fromEvent(window, 'load');

/**
 * Dispatches a custom event indicating a successful login.
 *
 * @param {object} data - The data returned from the login action.
 */
const dispatchOnLoginSuccessEvent = (data) => {
  const onLogIn = new CustomEvent('loginSuccess', {
    detail: {
      userData: data.User
    }
  });

  document.body.dispatchEvent(onLogIn);
};

/**
 * onAuthenticated: callback executed when Google One Tap successfully authenticated an user.
 * @param {object} googleResponse containing the information needed to authenticate the user in NewHomeSource.
 */
const onAuthenticated = async (googleResponse) => {
  const idToken = parseJsonWebToken(googleResponse.credential);
  const parameters = {
    payload: {
      locale: idToken.locale,
      picture: idToken.picture,
      email: idToken.email,
      name: idToken.name,
      givenName: idToken.given_name,
      familyName: idToken.family_name,
      subject: idToken.sub,
      audience: idToken.aud,
      emailVerified: idToken.email_verified,
      notBeforeTimeSeconds: idToken.nbf,
      hostedDomain: idToken.hd,
      issuer: idToken.iss,
      issuedAtTimeSeconds: idToken.iat,
      expirationTimeSeconds: idToken.exp,
      jwtId: idToken.jti,
    },
    keepMeLogged: true,
  };

  loginUserWithGoogleOneTap(parameters).subscribe((data) => {
    if (data?.Success) {
      dispatchOnLoginSuccessEvent(data);
      window.location.reload();
    }
  });
};

/**
 * Subscribe to the store$ observable to listen for changes.
 * If the user is not logged in and the Google One Tap library is not loaded, we proceed
 * and load the library so we gain access to Google One Tap capabilities.
 * This obserbable streams just one time since every time a user logges in/out a full
 * page reload happens, so there is no need to keep listening to the store$ while the user
 * navigates in NewHomeSource.
 */
const googleOneTapConfiguration$ = store$.pipe(
  filter((state) => selectIsLoggedIn(state) === false),
  filter((state) => selectGoogleOneTapLibraryLoadingStatus(state) !== LoadingStatus.Succeeded.name),
  map((state) => ({
    onLibraryLoaded,
    onLibraryErrored,
    onAuthenticated,
    clientId: selectConfigurationGoogleOneTapClientId(state),
  })),
  distinctUntilChanged(),
  filter((googleOneTapInformation) => !!googleOneTapInformation.clientId),
  take(1)
);

combineLatest([googleOneTapConfiguration$, onPageLoaded$])
  .pipe(map(([googleOneTapConfiguration]) => googleOneTapConfiguration))
  .subscribe(loadGoogleOneTap);

/**
 * Subscribe to the store$ observable to listen for changes.
 * If the user is not logged in and Google One Tap library is loaded, then we prompt the modal
 * inviting the user to log in using Google One Tap.
 * This obserbable streams just one time since every time a user logges in/out a full
 * page reload happens, so there is no need to keep listening to the store$ while the user
 * navigates in NewHomeSource.
 */
store$
  .pipe(
    filter((state) => selectIsLoggedIn(state) === false),
    filter((state) => selectGoogleOneTapLibraryLoadingStatus(state) === LoadingStatus.Succeeded.name),
    take(1)
  )
  .subscribe(showGoogleOneTapModal);

/**
 * Subscribe to the store$ observable to hide the Google One Tap modal
 * when a modal is opened.
 */
store$
  .pipe(
    filter((state) => Boolean(selectModal(state))),
    take(1)
  )
  .subscribe(hideGoogleOneTapModal);
